import type { InspectorSection, OptionId } from "src/api";
import { Btn, Icon, Tile, TileGlyph } from "../kit";
import { Explained } from "./Explained";
import { Tap } from "./Tap";
import {
  Between,
  Caption,
  Count,
  Head,
  Option,
  Options,
  RankGlyph,
  Ranks,
  RankTile,
  StatRow,
  TapRow,
  Taps,
  SectionNote,
  TileName,
  TileNote,
  Title,
  Wide
} from "./sectionStyles";

/**
 * One section, drawn where the popover used to draw it.
 *
 * Which sections there are, in what order and what each says is the
 * engine's (`InspectorSection`); this is the layout of each. **A list of things you could
 * make is a grid of tiles, not a table of rows** (§28.3), which is why saving,
 * climb and craft are tiles and a turret's stats — sentences, not words — are
 * full-width rows.
 */
export function Section({
  section,
  choose,
  beside
}: {
  section: InspectorSection;
  /** `session.choose`: the engine says what each tile does. */
  choose(option: OptionId): void;
  /** The inspector card, which every card in here hangs off. */
  beside: HTMLElement | null;
}) {
  switch (section.kind) {
    case "saving":
      return (
        <>
          <Title>{section.title}</Title>
          {/* Five ranks across a 240px card: a grid of equal columns rather
              than the rail's fixed 46px square, which is 230px of tile before
              a single gap and wrapped four-and-one. The tiles keep the rail's
              height and glyph — what changes is that they share the width they
              have. */}
          <Ranks>
            {section.options.map((option) => (
              <Explained key={option.id} item={option} beside={beside}>
                <RankTile
                  variant="rail"
                  data-testid={option.id}
                  armed={option.on}
                  aria-pressed={option.on}
                  disabled={!option.enabled}
                  onClick={() => choose(option.option)}
                >
                  <RankGlyph small className={option.on ? option.card?.state : undefined}>
                    {option.icon ? <Icon name={option.icon} size={19} /> : option.glyph}
                  </RankGlyph>
                </RankTile>
              </Explained>
            ))}
          </Ranks>
        </>
      );

    case "taps":
      return (
        <>
          <Title>{section.title}</Title>
          {/* The mock's `.taps`: a grade a row, In and Out as two switches,
              the count trailing. All four combinations are useful — a
              stockpile, a buffer, an open tap, or sealed — which is why they
              are two switches and never one three-state control. */}
          <Taps>
            <span />
            <Head>In</Head>
            <Head>Out</Head>
            <span />
            {section.rows.map((row) => (
              <TapRow key={row.grade}>
                <span>{row.name}</span>
                <Tap item={row.accept} choose={choose} beside={beside} />
                <Tap item={row.offer} choose={choose} beside={beside} />
                <Count>{row.count}</Count>
              </TapRow>
            ))}
          </Taps>
        </>
      );

    case "turret":
      return (
        <>
          <Title>{section.title}</Title>
          {section.rows.map((row) => (
            <StatRow key={row.id}>
              <Caption>{row.stat}</Caption>
              <Explained item={row} beside={beside}>
                <Wide
                  data-testid={row.id}
                  aria-pressed={row.on}
                  aria-disabled={row.enabled ? undefined : true}
                  onClick={row.enabled ? () => choose(row.option) : undefined}
                >
                  {row.label}
                </Wide>
              </Explained>
            </StatRow>
          ))}
        </>
      );

    case "climb": {
      // **One reason, said once.** With no Armory every row is off for the
      // same sentence, and four copies of it under four alike glyphs was a
      // wall that named nothing. A shared note goes above; each tile keeps
      // its name, and its own note only when they differ.
      const notes = new Set(section.tiles.map((tile) => tile.note));
      const shared = section.tiles.length > 1 && notes.size === 1 ? section.tiles[0].note : null;
      return (
        <>
          <Title>{section.title}</Title>
          {shared && <SectionNote data-testid="climb-note">{shared}</SectionNote>}
          <Options>
            {section.tiles.map((tile) => (
              <Option key={tile.id}>
                <Explained item={tile} beside={beside}>
                  <Tile
                    variant="rail"
                    data-testid={tile.id}
                    disabled={!tile.enabled}
                    onClick={() => choose(tile.option)}
                  >
                    <TileGlyph small>
                      {tile.icon ? <Icon name={tile.icon} size={19} /> : tile.glyph}
                    </TileGlyph>
                  </Tile>
                </Explained>
                <TileName>{tile.label}</TileName>
                {/* Always the reason, never a dead button. */}
                {!shared && <TileNote>{tile.note}</TileNote>}
              </Option>
            ))}
          </Options>
        </>
      );
    }

    case "sacrifice":
      // **A power is a mechanic, not a button** (§28.3): this surfaces on
      // the piece it would take apart, while the altar stands, rather than
      // as a pip in a row of things you happen to hold.
      return (
        <Between>
          <span>☠ {section.item.label} this piece</span>
          <Explained item={section.item} beside={beside}>
            <Btn small data-testid={section.item.id} onClick={() => choose(section.item.option)}>
              Aim
            </Btn>
          </Explained>
        </Between>
      );
  }
}
